import { useRef, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";

export function Field({ label, value, onChange, testId, textarea = false, hint }) {
  const Control = textarea ? "textarea" : "input";
  return <label className="cms-field" data-testid={`${testId}-label`}>{label}{hint && <small className="cms-hint">{hint}</small>}<Control value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} /></label>;
}

export function ImageField({ label, value, onChange, pin, testId, accept = "image/png,image/jpeg,image/webp,image/gif,image/svg+xml" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const { url } = await api.uploadMedia(pin, file); onChange(url); toast.success("Image uploaded. Save changes to publish."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div className="cms-image-field" data-testid={testId}>
      <div className="cms-image-preview" data-testid={`${testId}-preview`}>{value ? (/\.(mp4|webm)(\?|$)/i.test(value) ? <video src={value} muted /> : /youtu/.test(value) ? <span className="cms-video-tag">YouTube</span> : <img src={value} alt="" />) : <ImagePlus size={22} />}</div>
      <div className="cms-image-controls">
        <label className="cms-field">{label}<input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste an image URL or upload" data-testid={`${testId}-url-input`} /></label>
        <button type="button" className="cms-upload-button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testId}-upload-button`}>{busy ? <Loader2 size={15} className="spin" /> : <ImagePlus size={15} />} {busy ? "Uploading…" : accept.startsWith("video") ? "Upload video" : "Upload image"}</button>
        <input ref={inputRef} type="file" accept={accept} hidden onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testId}-file-input`} />
      </div>
    </div>
  );
}

const listToText = (items = []) => items.join("\n");
const textToList = (text = "") => text.split("\n").map((s) => s.trim()).filter(Boolean);

export function QuestionsEditor({ questions = [], onChange, pin }) {
  const [openId, setOpenId] = useState(questions[0]?.id);
  const updateQuestion = (index, patch) => onChange(questions.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  const updateOption = (qIndex, oIndex, patch) => updateQuestion(qIndex, { options: questions[qIndex].options.map((o, i) => (i === oIndex ? { ...o, ...patch } : o)) });
  return (
    <div className="cms-accordion" data-testid="advisor-cms-questions-editor">
      {questions.map((q, qi) => (
        <section className={`cms-block ${openId === q.id ? "open" : ""}`} key={q.id} data-testid={`advisor-cms-question-${q.id}`}>
          <button type="button" className="cms-block-head" onClick={() => setOpenId(openId === q.id ? "" : q.id)} data-testid={`advisor-cms-question-${q.id}-toggle`}><span className="cms-block-step">{q.progress_label}</span><b>{q.prompt}</b></button>
          {openId === q.id && (
            <div className="cms-block-body">
              <Field label="Progress label" value={q.progress_label} onChange={(v) => updateQuestion(qi, { progress_label: v, eyebrow: v.toUpperCase() })} testId={`advisor-cms-question-${q.id}-progress-input`} />
              <Field label="Question headline" value={q.prompt} onChange={(v) => updateQuestion(qi, { prompt: v })} testId={`advisor-cms-question-${q.id}-prompt-input`} textarea />
              <div className="cms-option-grid">
                {q.options.map((o, oi) => (
                  <article className="cms-option-card" key={o.id} data-testid={`advisor-cms-option-${o.id}`}>
                    <ImageField label="Card image" value={o.image_url} onChange={(v) => updateOption(qi, oi, { image_url: v })} pin={pin} testId={`advisor-cms-option-${o.id}-image`} />
                    <Field label="Card heading (3–4 words)" value={o.heading} onChange={(v) => updateOption(qi, oi, { heading: v })} testId={`advisor-cms-option-${o.id}-heading-input`} />
                    <Field label="Internal label" value={o.label} onChange={(v) => updateOption(qi, oi, { label: v })} testId={`advisor-cms-option-${o.id}-label-input`} />
                    <Field label="Why-this-fits priorities (one per line)" value={listToText(o.priorities)} onChange={(v) => updateOption(qi, oi, { priorities: textToList(v) })} testId={`advisor-cms-option-${o.id}-priorities-input`} textarea />
                    {q.id === "workplace" && <SceneEditor scene={o.scene || {}} onChange={(scene) => updateOption(qi, oi, { scene })} pin={pin} testId={`advisor-cms-scene-${o.id}`} />}
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function SceneEditor({ scene, onChange, pin, testId }) {
  const set = (key, value) => onChange({ ...scene, [key]: value });
  return (
    <div className="cms-scene" data-testid={testId}>
      <p className="cms-scene-title">Scenario video stage (shown after this card is chosen)</p>
      <Field label="Scene headline" value={scene.title} onChange={(v) => set("title", v)} testId={`${testId}-title-input`} />
      <Field label="Scene text" value={scene.body} onChange={(v) => set("body", v)} testId={`${testId}-body-input`} textarea />
      <ImageField label="Video (MP4/WEBM upload, or paste a YouTube / MP4 URL)" value={scene.video_url} onChange={(v) => set("video_url", v)} pin={pin} testId={`${testId}-video`} accept="video/mp4,video/webm" />
      <ImageField label="Poster image (shown when no video)" value={scene.poster_url} onChange={(v) => set("poster_url", v)} pin={pin} testId={`${testId}-poster`} />
      <Field label="Button label" value={scene.cta_label} onChange={(v) => set("cta_label", v)} testId={`${testId}-cta-input`} />
    </div>
  );
}

export function ProductsEditor({ products = [], onChange, pin }) {
  const [openId, setOpenId] = useState(products[0]?.id);
  const update = (index, patch) => onChange(products.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  return (
    <div className="cms-accordion" data-testid="advisor-cms-products-editor">
      {products.map((p, i) => (
        <section className={`cms-block ${openId === p.id ? "open" : ""}`} key={p.id} data-testid={`advisor-cms-product-${p.id}`}>
          <button type="button" className="cms-block-head" onClick={() => setOpenId(openId === p.id ? "" : p.id)} data-testid={`advisor-cms-product-${p.id}-toggle`}><span className="cms-block-step">{p.series}</span><b>{p.brand_name || p.name}</b></button>
          {openId === p.id && (
            <div className="cms-block-body cms-product-body">
              <ImageField label="Product image (transparent PNG)" value={p.image_url} onChange={(v) => update(i, { image_url: v })} pin={pin} testId={`advisor-cms-product-${p.id}-image`} />
              <div className="cms-form-grid">
                <Field label="Headline name" value={p.brand_name} onChange={(v) => update(i, { brand_name: v })} testId={`advisor-cms-product-${p.id}-brand-input`} />
                <Field label="Series" value={p.series} onChange={(v) => update(i, { series: v })} testId={`advisor-cms-product-${p.id}-series-input`} />
                <Field label="Tagline" value={p.tagline} onChange={(v) => update(i, { tagline: v })} testId={`advisor-cms-product-${p.id}-tagline-input`} textarea />
                <Field label="Best for" value={p.best_for} onChange={(v) => update(i, { best_for: v })} testId={`advisor-cms-product-${p.id}-best-for-input`} />
                <Field label="Features (one per line)" value={listToText(p.features)} onChange={(v) => update(i, { features: textToList(v) })} testId={`advisor-cms-product-${p.id}-features-input`} textarea />
                <Field label="Also consider" value={p.also_consider} onChange={(v) => update(i, { also_consider: v })} testId={`advisor-cms-product-${p.id}-also-input`} />
              </div>
              {p.hotspots && <HotspotsEditor hotspots={p.hotspots} onChange={(hotspots) => update(i, { hotspots })} pin={pin} testId={`advisor-cms-product-${p.id}-hotspots`} />}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function HotspotsEditor({ hotspots, onChange, pin, testId }) {
  const points = hotspots.points || [];
  const updatePoint = (index, patch) => onChange({ ...hotspots, points: points.map((pt, i) => (i === index ? { ...pt, ...patch } : pt)) });
  return (
    <div className="cms-scene" data-testid={testId}>
      <p className="cms-scene-title">Interactive key-feature hotspots (shown on the result screen under the "Key features" tab)</p>
      <ImageField label="Lifestyle photo behind the numbered hotspots" value={hotspots.image_url} onChange={(v) => onChange({ ...hotspots, image_url: v })} pin={pin} testId={`${testId}-image`} />
      <div className="cms-option-grid">
        {points.map((pt, i) => (
          <article className="cms-option-card" key={i} data-testid={`${testId}-point-${i + 1}`}>
            <p className="cms-scene-title">Hotspot {i + 1}</p>
            <Field label="Feature title" value={pt.title} onChange={(v) => updatePoint(i, { title: v })} testId={`${testId}-point-${i + 1}-title-input`} />
            <Field label="Feature text" value={pt.body} onChange={(v) => updatePoint(i, { body: v })} testId={`${testId}-point-${i + 1}-body-input`} textarea />
            <div className="cms-form-grid">
              <Field label="Position left (%)" value={String(pt.x ?? "")} onChange={(v) => updatePoint(i, { x: Number(v) || 0 })} testId={`${testId}-point-${i + 1}-x-input`} hint="0 = left edge, 100 = right edge" />
              <Field label="Position top (%)" value={String(pt.y ?? "")} onChange={(v) => updatePoint(i, { y: Number(v) || 0 })} testId={`${testId}-point-${i + 1}-y-input`} hint="0 = top edge, 100 = bottom edge" />
            </div>
            <ImageField label="Side image (transparent PNG)" value={pt.image_url} onChange={(v) => updatePoint(i, { image_url: v })} pin={pin} testId={`${testId}-point-${i + 1}-image`} />
          </article>
        ))}
      </div>
    </div>
  );
}

const COPY_SECTIONS = [
  ["prequiz", "Entry form (before question 1)", { eyebrow: "Pill label", title: "Headline", subtitle: "Supporting text", submit_label: "Button label", assurance: "Footnote under button" }],
  ["returning", "Returning visitor sheet", { eyebrow: "Pill label", title: "Headline ({first} = first name)", subtitle: "Supporting text", continue_label: "Primary button ({first})", switch_label: "Secondary link ({first})" }],
  ["demo", "Demo & offer form", { eyebrow: "Pill label", title: "Headline", subtitle: "Supporting text", vendor_label: "Headset vendor question", submit_label: "Button label" }],
  ["consent", "Consent block", { intro: "Intro line (keep the words 'Privacy & Terms' for the link)", note: "Note under switches" }],
  ["result", "Result page", { kicker: "Small label above headset name", why_heading: "'Why this fits' heading", also_consider_label: "Also-consider label", demo_cta: "Primary button", share_cta: "Share button", compare_cta: "Compare button", visual_tab_product: "Visual tab 1 label (product shot)", visual_tab_features: "Visual tab 2 label (interactive hotspots)" }],
];

export function CopyEditor({ copy = {}, onChange }) {
  const update = (section, key, value) => onChange({ ...copy, [section]: { ...(copy[section] || {}), [key]: value } });
  return (
    <div className="cms-accordion" data-testid="advisor-cms-copy-editor">
      {COPY_SECTIONS.map(([section, title, fields]) => (
        <section className="cms-block open" key={section} data-testid={`advisor-cms-copy-${section}`}>
          <div className="cms-block-head static"><b>{title}</b></div>
          <div className="cms-block-body cms-form-grid">
            {Object.entries(fields).map(([key, label]) => <Field key={key} label={label} value={copy[section]?.[key]} onChange={(v) => update(section, key, v)} testId={`advisor-cms-copy-${section}-${key}-input`} textarea={key === "subtitle" || key === "intro"} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
